<script setup lang="ts">
import type { ExclusionUpdate } from '@rialtic/types'

type ExclusionVersionConnectorItem = {
  status: string
  created_at?: string
  connector: { id: string; name: string; created_at: string }
}

type ConnectorStatusUpdate = {
  connector_id: string
  status: string
}

const showStatusEditor = defineModel<boolean>({ default: false })

const props = withDefaults(
  defineProps<{
    exclusionId: string
    exclusionName: string
    connectors?: ExclusionVersionConnectorItem[]
  }>(),
  { connectors: () => [] },
)

const latestConnectorStatusCreatedAt = computed(() => {
  let latest
  for (const connectorStatus of props.connectors) {
    if (!connectorStatus.created_at) continue
    if (!latest || new Date(connectorStatus.created_at) > new Date(latest)) {
      latest = connectorStatus.created_at
    }
  }
  return latest
})

const emit = defineEmits<{
  save: [value: ExclusionUpdate]
}>()

const sortedConnectors = computed(() =>
  [...(props.connectors || [])].sort((a, b) =>
    a.connector.name.localeCompare(b.connector.name),
  ),
)

const initialStatuses = computed(
  () =>
    new Map(
      sortedConnectors.value.map((connector) => [
        connector.connector.id,
        connector.status,
      ]),
    ),
)

const statuses = ref<Map<string, string>>(new Map())

const resetStatuses = () => {
  statuses.value = new Map(initialStatuses.value)
}

watch(() => props.connectors, resetStatuses, {
  immediate: true,
})

const updateStatus = (connectorId: string, status: string) => {
  statuses.value.set(connectorId, status)
}

const hasChanges = computed(() => {
  if (statuses.value.size !== initialStatuses.value.size) return true

  for (const [connectorId, status] of initialStatuses.value) {
    if (statuses.value.get(connectorId) !== status) return true
  }

  return false
})

const cancel = () => {
  resetStatuses()
  showStatusEditor.value = false
}

const saveStatus = () => {
  const allConnectorStatuses: ConnectorStatusUpdate[] = []

  for (const [connectorId, status] of statuses.value) {
    allConnectorStatuses.push({ connector_id: connectorId, status })
  }

  emit('save', {
    id: props.exclusionId,
    payload: { connector_statuses: allConnectorStatuses },
  })
}
</script>

<template>
  <UiDialog v-model="showStatusEditor" max-width="860px">
    <div
      class="flex flex-col p-2 lg:px-10 lg:py-8"
      data-testid="status_container_all_lob"
    >
      <header>
        <div class="flex items-start justify-between">
          <div>
            <p class="h1 text-primary-900 mb-1">Exclusion Status Selection</p>
            <p class="body-1 text-neutral-700">
              {{ exclusionName }}
            </p>
          </div>
          <button
            class="rounded-full p-1 text-neutral-700"
            aria-label="Close status editor"
            @click="cancel()"
          >
            <div class="i-ph-x-bold" />
          </button>
        </div>
      </header>

      <div class="my-4 h-px w-full bg-neutral-200" />

      <ul class="flex flex-col space-y-2">
        <li class="subtitle-1 flex px-2 font-black">
          <div class="w-[200px]">Line of Business</div>
          <div class="w-[550px]">Status</div>
        </li>
        <li
          v-for="connector in sortedConnectors"
          :key="connector.connector.id"
          class="flex items-center gap-4 px-2 py-1"
        >
          <div class="flex w-[200px] shrink-0 items-center gap-2">
            <p
              class="min-w-0 truncate text-sm leading-relaxed text-neutral-800"
              :title="connector.connector.name"
            >
              {{ connector.connector.name }}
            </p>
            <span
              v-if="
                latestConnectorStatusCreatedAt &&
                new Date(connector.connector.created_at) >
                  new Date(latestConnectorStatusCreatedAt)
              "
              class="bg-primary-600 shrink-0 rounded-full px-2 py-0.5 text-xs text-white"
              data-testid="new-connector"
            >
              New
            </span>
          </div>
          <div class="w-[550px] shrink-0">
            <div role="group" class="flex rounded-md shadow-sm">
              <button
                :name="`archived-${connector.connector.id}`"
                class="focus:ring-primary relative flex flex-1 items-center justify-center rounded-l-lg border border-neutral-200 px-3 py-2 text-xs transition-colors focus:z-10 focus:ring-2"
                :class="{
                  'bg-neutral-100 text-neutral-800':
                    statuses.get(connector.connector.id) === 'Archived',
                  'bg-white text-neutral-700':
                    statuses.get(connector.connector.id) !== 'Archived',
                  'hover:text-primary hover:bg-neutral-50':
                    statuses.get(connector.connector.id) !== 'Archived',
                }"
                type="button"
                @click="updateStatus(connector.connector.id, 'Archived')"
              >
                Archived
              </button>
              <button
                :name="`not-running-${connector.connector.id}`"
                class="focus:ring-primary relative flex flex-1 items-center justify-center border border-neutral-200 px-3 py-2 text-xs transition-colors focus:z-10 focus:ring-2"
                :class="{
                  'bg-neutral-300 text-neutral-900':
                    statuses.get(connector.connector.id) === 'Stopped',
                  'bg-white text-neutral-700':
                    statuses.get(connector.connector.id) !== 'Stopped',
                  'hover:text-primary hover:bg-neutral-50':
                    statuses.get(connector.connector.id) !== 'Stopped',
                }"
                type="button"
                @click="updateStatus(connector.connector.id, 'Stopped')"
              >
                Not running
              </button>
              <button
                :name="`running-${connector.connector.id}`"
                class="focus:ring-primary relative flex flex-1 items-center justify-center rounded-r-md border border-neutral-200 px-3 py-2 text-xs transition-colors focus:z-10 focus:ring-2"
                :class="{
                  'bg-tertiary-700 text-white':
                    statuses.get(connector.connector.id) === 'Running',
                  'bg-white text-neutral-700':
                    statuses.get(connector.connector.id) !== 'Running',
                  'hover:text-primary hover:bg-neutral-50':
                    statuses.get(connector.connector.id) !== 'Running',
                }"
                type="button"
                @click="updateStatus(connector.connector.id, 'Running')"
              >
                Running
              </button>
            </div>
          </div>
        </li>
      </ul>

      <div class="my-4 h-px w-full bg-neutral-200" />

      <footer>
        <div class="flex w-full items-center justify-end space-x-2">
          <UiButton
            size="sm"
            variant="outlined"
            data-testid="status_all_lob_cancel"
            @click="cancel"
          >
            Close
          </UiButton>
          <UiButton
            :disabled="!hasChanges"
            size="sm"
            data-testid="status_all_lob_confirm"
            @click="saveStatus"
          >
            Confirm changes
          </UiButton>
        </div>
      </footer>
    </div>
  </UiDialog>
</template>
